export type VideoModel =
  | 'gemini-omni-1.1-flash'
  | 'veo-3.1'
  | 'veo-3.1-fast'
  | 'runway-gen-4'
  | 'runway-gen-4.5';

export type VeoAspectRatio = '16:9' | '9:16';
export type Resolution = '720p' | '1080p';
export type RunwayAspectRatio =
  | '1280:720'
  | '720:1280'
  | '1104:832'
  | '832:1104'
  | '960:960'
  | '1584:672';

export type VideoFrame = {
  assetId: string;
  metadata: { size: number; fileName: string };
  mimeType: string;
  url?: string;
};

type VideoFrameExtras = {
  startFrame?: VideoFrame | null;
  endFrame?: VideoFrame | null;
};

export type VideoOptions = {
  settings: VideoSettings;
};

export type VideoSettings =
  | ({
      model: 'veo-3.1';
      aspectRatio: VeoAspectRatio;
      resolution: Resolution;
      durationSeconds: number;
    } & VideoFrameExtras)
  | ({
      model: 'veo-3.1-fast';
      aspectRatio: VeoAspectRatio;
      resolution: Resolution;
      durationSeconds: number;
    } & VideoFrameExtras)
  | ({
      model: 'gemini-omni-1.1-flash';
      aspectRatio: VeoAspectRatio;
      resolution: Resolution;
      durationSeconds: number;
    } & VideoFrameExtras)
  | ({
      model: 'runway-gen-4';
      aspectRatio: RunwayAspectRatio;
      durationSeconds: number;
    } & VideoFrameExtras)
  | ({
      model: 'runway-gen-4.5';
      aspectRatio: RunwayAspectRatio;
      durationSeconds: number;
    } & VideoFrameExtras);

/* eslint-disable i18next/no-literal-string -- vendor model and provider names */
export const SUPPORTED_VIDEO_MODELS: Array<{
  label: string;
  value: VideoModel;
  provider?: string;
}> = [
  { label: 'Veo 3.1', value: 'veo-3.1', provider: 'Google' },
  { label: 'Veo 3.1 Fast', value: 'veo-3.1-fast', provider: 'Google' },
  { label: 'Gemini Omni 1.1 Flash', value: 'gemini-omni-1.1-flash', provider: 'Google' },
  { label: 'Runway Gen 4', value: 'runway-gen-4', provider: 'Runway' },
  { label: 'Runway Gen 4.5', value: 'runway-gen-4.5', provider: 'Runway' },
];

export const VIDEO_TO_VIDEO_MODELS: Array<{
  label: string;
  value: VideoModel;
  provider?: string;
}> = [{ label: 'Runway Gen 4', value: 'runway-gen-4', provider: 'Runway' }];
/* eslint-enable i18next/no-literal-string */

export const VEO_ASPECT_RATIOS: Array<{
  label: string;
  value: VeoAspectRatio;
  orientation: 'landscape' | 'portrait';
}> = [
  { label: '16:9', value: '16:9', orientation: 'landscape' },
  { label: '9:16', value: '9:16', orientation: 'portrait' },
];

export const RUNWAY_ASPECT_RATIOS: Array<{
  label: string;
  value: RunwayAspectRatio;
  orientation: 'landscape' | 'portrait';
}> = [
  { label: '1280:720', value: '1280:720', orientation: 'landscape' },
  { label: '720:1280', value: '720:1280', orientation: 'portrait' },
  { label: '1104:832', value: '1104:832', orientation: 'landscape' },
  { label: '832:1104', value: '832:1104', orientation: 'portrait' },
  { label: '960:960', value: '960:960', orientation: 'portrait' },
  { label: '1584:672', value: '1584:672', orientation: 'landscape' },
];

/**
 * Aspect ratios supported by Runway text-to-video (no start frame). When a
 * start frame is present the full {@link RUNWAY_ASPECT_RATIOS} set applies.
 */
export const RUNWAY_TEXT_TO_VIDEO_ASPECT_RATIOS = RUNWAY_ASPECT_RATIOS.filter(
  (r) => r.value === '1280:720' || r.value === '720:1280',
);

export const RESOLUTIONS_FOR_ASPECT: Record<VeoAspectRatio, Resolution[]> = {
  '16:9': ['720p', '1080p'],
  '9:16': ['720p'],
};

/**
 * The image types veo consumes as reference images, mirroring the backend's
 * SUPPORTED_IMAGE_TYPES. Anything else the composer accepts is filtered out server-side and
 * never reaches the provider, so it must not constrain the duration here.
 */
export const VEO_REFERENCE_IMAGE_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];

export const DURATIONS: Array<{ label: string; value: number }> = [
  { label: '4s', value: 4 },
  { label: '6s', value: 6 },
  { label: '8s', value: 8 },
];

/** Omni takes any length from 3 to 10 seconds and has no duration constraints. */
export const OMNI_DURATIONS: Array<{ label: string; value: number }> = [
  { label: '4s', value: 4 },
  { label: '6s', value: 6 },
  { label: '8s', value: 8 },
  { label: '10s', value: 10 },
];

export const OMNI_RESOLUTIONS: Resolution[] = ['720p', '1080p'];

/**
 * Configuration for video model frame capabilities
 * Defines which models support start and/or end frame inputs
 */
export type VideoModelConfig = {
  /** Whether the model supports a start frame/image input for video generation */
  enableStartFrame?: boolean;
  /** Whether the model supports an end frame input for video generation */
  enableEndFrame?: boolean;
};

/**
 * Frame support configuration for each video model
 *
 * Frame Support Matrix:
 * - Veo 3.1: Both start and end frames
 * - Runway Gen 4: Start frame only (supports image-to-video and video-to-video)
 * @see
 * https://ai.google.dev/gemini-api/docs/video?example=dialogue#model-features
 * https://docs.dev.runwayml.com/
 */
export const VIDEO_MODEL_CONFIG: Record<VideoModel, VideoModelConfig> = {
  'gemini-omni-1.1-flash': {
    enableStartFrame: true,
    enableEndFrame: true,
  },
  'veo-3.1': {
    enableStartFrame: true,
    enableEndFrame: true,
  },
  'veo-3.1-fast': {
    enableStartFrame: true,
    enableEndFrame: true,
  },
  'runway-gen-4': {
    enableStartFrame: true,
    enableEndFrame: false,
  },
  'runway-gen-4.5': {
    enableStartFrame: true,
    enableEndFrame: false,
  },
};
